import { useState, useRef, useEffect, useCallback } from "react";

import { SpinnerIcon } from "~/components/icon";
import { useCustomerSearch } from "~/hooks/useCustomerSearch";
import type { CustomerSearchResult } from "~/types";

import { CustomerResultsList } from "./CustomerResultsList";

interface CustomerPickerProps {
  id?: string;
  disabled?: boolean;
  value: CustomerSearchResult | null;
  onChange: (customer: CustomerSearchResult | null) => void;
}

/**
 * Search-as-you-type customer picker for forms. Controlled: the parent owns
 * `value` (the selected customer, or null) and updates it via `onChange`.
 *
 * Renders inline, not floating: it lives inside a Radix Dialog, which clips
 * absolute/portaled overflow and blocks pointer events outside its own
 * subtree.
 */
export function CustomerPicker({
  id = "customer_id",
  disabled,
  value,
  onChange,
}: CustomerPickerProps) {
  const [query, setQuery] = useState(value?.name ?? "");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { results, loading, highlightedIndex, setHighlightedIndex } =
    useCustomerSearch(query, !value && query.length >= 2);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectCustomer = useCallback(
    (customer: CustomerSearchResult) => {
      setQuery(customer.name);
      setOpen(false);
      onChange(customer);
    },
    [onChange],
  );

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        type="text"
        value={query}
        disabled={disabled}
        onChange={(e) => {
          if (value) {
            onChange(null);
          }
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || loading || results.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((i) => Math.min(i + 1, results.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            selectCustomer(results[highlightedIndex]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="Search for a customer..."
        autoComplete="off"
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {loading ? (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <SpinnerIcon className="h-4 w-4 animate-spin text-gray-400" />
        </div>
      ) : null}

      {open && query.length > 0 && !value ? (
        <div className="relative z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {query.length < 2 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              Type at least 2 characters...
            </div>
          ) : loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No customers found
            </div>
          ) : (
            <CustomerResultsList
              results={results}
              highlightedIndex={highlightedIndex}
              onHighlight={setHighlightedIndex}
              onSelect={selectCustomer}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}
